import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Shield, Download, Search, X, LogIn, FileDown, Eye, AlertTriangle } from 'lucide-react';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import { c, t, sp, r, btn, card, tbl } from '../theme';

// Who signed in, what they opened, and what they took out.
//
// super_admin only, and worth being deliberate about: this is a record of what
// named colleagues did and when. It exists because the portal holds
// named-employee wage data, where knowing who read what is close to a
// requirement — not because activity is interesting on its own.
//
// The log is append-only. Nothing on this page writes to it, and nothing
// should: a log somebody can edit is not evidence of anything.

// Actions are grouped rather than listed raw, because a reader scanning this
// wants "logins" or "exports", not eleven similar-looking constants.
const ACTION_META = {
  LOGIN_SUCCEEDED: { label: 'Signed in',        tone: 'neutral', icon: LogIn },
  LOGIN_FAILED:    { label: 'Sign-in failed',   tone: 'warning', icon: AlertTriangle },
  LOGIN_DENIED:    { label: 'Sign-in denied',   tone: 'danger',  icon: AlertTriangle },
  REPORT_VIEWED:   { label: 'Viewed',           tone: 'neutral', icon: Eye },
  REPORT_EXPORTED: { label: 'Exported',         tone: 'accent',  icon: FileDown },
  REPORT_ACCESS_DENIED: { label: 'Refused',     tone: 'danger',  icon: AlertTriangle },
};

const MODULE_LABELS = {
  inventory: 'Inventory', overtime: 'Overtime', fuel_report: 'Fuel Report',
  time_wage_audit: 'Time & Wage Audit', expense_analysis: 'Expense Analysis',
  city_pricing: 'City Pricing', staffing: 'Staffing Tracker', access_log: 'Access Log',
  sales_report: 'Sales Report', customer_billing: 'Customer Billing',
  post_collections: 'Post Collections',
  contact_center: 'Contact Center',
  usage: 'Usage',
};

const TONES = {
  neutral: { bg: c.surfaceSunk, fg: c.textMuted },
  accent:  { bg: '#EEF4FF',     fg: c.navy },
  warning: { bg: c.warningTint, fg: c.warningText },
  danger:  { bg: c.brandTint,   fg: c.dangerText },
};

// What the Detail column says about one event.
//
// MIRRORED in routes/accessLogAdmin.js for the workbook, not shared -- frontend
// and backend are separate packages. The two can drift; the backend test pins
// the behaviour, so change both together.
//
// The log holds two very different kinds of row. Access events (views, exports,
// refusals) carry a `filter` — the thing actually asked for, which is the whole
// question someone brings to this page. Mutation events carry their own shape
// instead: a permission grant records the email of the person granted, which is
// the most important fact in the row and was previously displayed as an em dash
// because this only knew about filter/reason/method.
//
// So: the filter when there is one, otherwise whatever else the row carries.
// Nulls are skipped rather than rendered as "to=null" — old rows still hold
// them, since the log is append-only and cannot be rewritten to tidy them up.
const DETAIL_SKIP = new Set(['module', 'path', 'filter']);

function describeDetails(details) {
  if (!details) return null;

  const pairs = [];
  const push = (k, v) => {
    if (v === null || v === undefined || v === '') return;
    if (typeof v === 'object') return;
    pairs.push(`${k}=${String(v).slice(0, 60)}`);
  };

  for (const [k, v] of Object.entries(details.filter || {})) push(k, v);
  if (!pairs.length) {
    for (const [k, v] of Object.entries(details)) {
      if (DETAIL_SKIP.has(k)) continue;
      push(k, v);
    }
  }
  // Four is what fits before the cell ellipsizes; the rest is in the tooltip,
  // so nothing is hidden without a way to reach it.
  return pairs.length ? pairs.slice(0, 4).join('  ') : null;
}

function isoDaysAgo(days) {
  return new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
}

export default function AccessLog() {
  const [filters, setFilters] = useState({
    from: isoDaysAgo(30), to: '', user_id: '', action: '', module: '', q: '',
  });
  const [offset, setOffset]   = useState(0);
  const [search, setSearch]   = useState('');
  const [exporting, setExporting] = useState(false);
  const LIMIT = 100;

  const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));

  const { data, isLoading, error } = useQuery({
    queryKey: ['access-log', params, offset],
    queryFn:  () => api.get('/admin/access-log', { params: { ...params, limit: LIMIT, offset } }),
  });

  const { data: opts } = useQuery({
    queryKey: ['access-log-filters'],
    queryFn:  () => api.get('/admin/access-log/filters'),
  });

  function set(key, value) {
    setFilters((f) => ({ ...f, [key]: value }));
    setOffset(0);                       // a new filter with an old page number
  }                                     // shows page 3 of a different question

  async function runExport() {
    setExporting(true);
    try {
      const qs   = new URLSearchParams(params).toString();
      const blob = await api.getBlob(`/admin/access-log/export?${qs}`);
      const url  = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `access-log-${filters.from}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message || 'Export failed.');
    } finally {
      setExporting(false);
    }
  }

  const items   = data?.items || [];
  const cleared = filters.from === isoDaysAgo(30) && !filters.to && !filters.user_id
    && !filters.action && !filters.module && !filters.q;

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Administration"
        title="Access Log"
        subtitle="Sign-ins, report views and exports, by user and time. Append-only — nothing here can be edited or removed."
        actions={
          <button style={s.exportBtn} onClick={runExport} disabled={exporting || !items.length}>
            <Download size={13} /> {exporting ? 'Preparing…' : 'Export to Excel'}
          </button>
        }
      />

      {/* ── Filters ──────────────────────────────────────────── */}
      <div style={s.filterRow}>
        <div style={s.group}>
          <label style={s.label} htmlFor="al-from">From</label>
          <input id="al-from" type="date" style={s.input}
            value={filters.from} onChange={(e) => set('from', e.target.value)} />
        </div>
        <div style={s.group}>
          <label style={s.label} htmlFor="al-to">To</label>
          <input id="al-to" type="date" style={s.input}
            value={filters.to} onChange={(e) => set('to', e.target.value)} />
        </div>
        <div style={s.group}>
          <label style={s.label} htmlFor="al-user">User</label>
          <select id="al-user" style={s.select}
            value={filters.user_id} onChange={(e) => set('user_id', e.target.value)}>
            <option value="">Everyone</option>
            {(opts?.users || []).filter((u) => u.events > 0).map((u) => (
              <option key={u.id} value={u.id}>{u.display_name} ({u.events})</option>
            ))}
          </select>
        </div>
        <div style={s.group}>
          <label style={s.label} htmlFor="al-action">Action</label>
          <select id="al-action" style={s.select}
            value={filters.action} onChange={(e) => set('action', e.target.value)}>
            <option value="">Any action</option>
            {(opts?.actions || []).map((a) => (
              <option key={a.action} value={a.action}>
                {ACTION_META[a.action]?.label || a.action} ({a.n})
              </option>
            ))}
          </select>
        </div>
        <div style={s.group}>
          <label style={s.label} htmlFor="al-module">Report</label>
          <select id="al-module" style={s.select}
            value={filters.module} onChange={(e) => set('module', e.target.value)}>
            <option value="">Any report</option>
            {(opts?.modules || []).map((m) => (
              <option key={m.module} value={m.module}>
                {MODULE_LABELS[m.module] || m.module} ({m.n})
              </option>
            ))}
          </select>
        </div>
        <div style={s.group}>
          <label style={s.label} htmlFor="al-q">Search</label>
          <form onSubmit={(e) => { e.preventDefault(); set('q', search); }}>
            <div style={s.searchWrap}>
              <Search size={13} color={c.textFaint} />
              <input id="al-q" style={s.searchInput} placeholder="name, email or IP"
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </form>
        </div>
        {!cleared && (
          <button style={s.clearBtn} onClick={() => {
            setFilters({ from: isoDaysAgo(30), to: '', user_id: '', action: '', module: '', q: '' });
            setSearch(''); setOffset(0);
          }}>
            <X size={12} /> Clear
          </button>
        )}
      </div>

      {data && (
        <div style={s.summary}>
          <strong>{Number(data.totals?.total || 0).toLocaleString('en-US')}</strong> events
          {' · '}<strong>{data.totals?.distinct_users || 0}</strong> people
          {' · '}from {data.from}{data.to ? ` to ${data.to}` : ' to now'}
        </div>
      )}

      {isLoading ? <LoadingSpinner /> : error ? (
        <div style={s.card}><div style={s.err}>{error.message}</div></div>
      ) : !items.length ? (
        <div style={s.card}><div style={s.empty}>No events match these filters.</div></div>
      ) : (
        <div style={s.card}>
          <div style={s.scroll}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>When</th>
                  <th style={s.th}>User</th>
                  <th style={s.th}>Action</th>
                  <th style={s.th}>Report</th>
                  <th style={s.th}>Detail</th>
                  <th style={s.th}>IP</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  const meta = ACTION_META[row.action]
                    || { label: row.action, tone: 'neutral', icon: Shield };
                  const tone = TONES[meta.tone];
                  const Icon = meta.icon;
                  const detail = describeDetails(row.details);
                  return (
                    <tr key={row.id} style={s.tr}>
                      <td style={s.tdWhen} title={row.created_at}>
                        {new Date(row.created_at).toLocaleString('en-US', {
                          month: 'short', day: 'numeric', hour: 'numeric',
                          minute: '2-digit', timeZone: 'America/Los_Angeles',
                        })}
                      </td>
                      <td style={s.td}>
                        {/* A deleted user leaves rows behind — audit_log's FK
                            keeps the id, so say so rather than render blank. */}
                        <div style={s.userName}>{row.display_name || '(deleted user)'}</div>
                        <div style={s.userEmail}>{row.email || row.user_id || '—'}</div>
                      </td>
                      <td style={s.td}>
                        <span style={{ ...s.chip, background: tone.bg, color: tone.fg }}>
                          <Icon size={11} /> {meta.label}
                        </span>
                      </td>
                      <td style={s.td}>
                        {row.details?.module
                          ? (MODULE_LABELS[row.details.module] || row.details.module)
                          : <span style={s.dim}>—</span>}
                      </td>
                      <td
                        style={s.tdDetail}
                        title={[row.details?.path, JSON.stringify(row.details || {})]
                          .filter(Boolean).join('\n')}
                      >
                        {detail || <span style={s.dim}>—</span>}
                      </td>
                      <td style={s.tdIp} title={row.user_agent || ''}>
                        {row.ip_address || <span style={s.dim}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={s.foot}>
            <span>
              Showing {offset + 1}–{offset + items.length}
              {data?.has_more ? ' of more' : ''}
            </span>
            <div style={s.pager}>
              <button style={s.pageBtn} disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - LIMIT))}>Previous</button>
              <button style={s.pageBtn} disabled={!data?.has_more}
                onClick={() => setOffset(offset + LIMIT)}>Next</button>
            </div>
          </div>
        </div>
      )}

      <div style={s.note}>
        Times are Pacific. A view is recorded once per person per report every 30
        minutes, so a single visit is one line rather than one per API call.
        Exports and refusals are always recorded individually, with the filter
        that produced them — for refusals, repetition is the signal. An expired
        session is not a refusal and appears as a sign-in row instead.
      </div>
    </div>
  );
}

const s = {
  page: { padding: `${sp[6]} ${sp[7]}`, maxWidth: '1400px', margin: '0 auto' },

  filterRow: {
    display: 'flex', gap: sp[3], flexWrap: 'wrap', alignItems: 'flex-end',
    marginBottom: sp[4],
  },
  group: { display: 'flex', flexDirection: 'column', gap: '4px' },
  label: { ...t.micro, fontWeight: 600, color: c.textMuted },
  input: {
    padding: '7px 10px', border: `1px solid ${c.borderStrong}`, borderRadius: r.md,
    ...t.bodySm, color: c.textStrong, fontFamily: 'inherit', background: c.surface,
  },
  select: {
    padding: '7px 10px', border: `1px solid ${c.borderStrong}`, borderRadius: r.md,
    ...t.bodySm, color: c.textStrong, fontFamily: 'inherit', minWidth: '160px',
    background: c.surface,
  },
  searchWrap: {
    display: 'flex', alignItems: 'center', gap: sp[2], padding: '7px 10px',
    border: `1px solid ${c.borderStrong}`, borderRadius: r.md, background: c.surface,
  },
  searchInput: {
    border: 'none', outline: 'none', ...t.bodySm, fontFamily: 'inherit',
    color: c.textStrong, width: '150px', background: 'transparent',
  },
  clearBtn:  { ...btn.secondaryXs, background: c.surface, gap: sp[1] },
  exportBtn: { ...btn.secondarySm, gap: sp[1] },

  summary: { ...t.bodySm, color: c.textMuted, marginBottom: sp[3] },

  card:   { ...card.base, overflow: 'hidden' },
  scroll: { overflowX: 'auto' },
  table:  { width: '100%', borderCollapse: 'collapse' },
  th:     { ...tbl.th, whiteSpace: 'nowrap' },
  tr:     { ...tbl.tr },
  td:     { ...tbl.td, whiteSpace: 'nowrap' },
  tdWhen: { ...tbl.td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: c.textMuted },
  tdIp:   { ...tbl.td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: c.textMuted },
  tdDetail: {
    ...tbl.td, maxWidth: '320px', overflow: 'hidden',
    textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: c.textMuted,
  },
  userName:  { ...t.bodySm, color: c.textStrong, fontWeight: 600 },
  userEmail: { ...t.micro, color: c.textFaint },
  dim:       { color: c.textFaint },

  chip: {
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    padding: '3px 8px', borderRadius: r.pill, ...t.micro, fontWeight: 600,
    whiteSpace: 'nowrap',
  },

  foot: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: `${sp[3]} ${sp[4]}`, ...t.micro, color: c.textFaint,
    borderTop: `1px solid ${c.border}`, background: c.surfaceSunk,
  },
  pager:   { display: 'flex', gap: sp[2] },
  pageBtn: { ...btn.secondaryXs },

  note:   { ...t.micro, color: c.textFaint, marginTop: sp[4], lineHeight: 1.6, maxWidth: '80ch' },
  empty:  { padding: sp[8], textAlign: 'center', ...t.bodySm, color: c.textFaint },
  err:    { padding: sp[5], ...t.bodySm, color: c.danger },
};
